<template>
  <div class="uk-container">
    <div class="uk-position-center" v-if="waitingForUserData">
      <div uk-spinner></div>
    </div>

    <!-- Products Section -->
    <div class="uk-section" v-if="products.length > 0">
      <h2 class="uk-margin">Your Licenses</h2>
      <div class="uk-card uk-card-default uk-card-hover" v-for="product in products" :key="product.name + product.version">
        <div class="uk-card-header uk-position-relative">
          <div class="uk-grid-small uk-flex-middle" uk-grid>
              <div class="uk-width-expand">
                <h3 class="uk-card-title">
                  {{fullProductName(product)}}
                </h3>
              </div>
              <div class="uk-width-auto uk-text-center">
                <div v-for="(release, platform) in latestReleasePerPlatform(product)"
                  v-if="release"
                  :key="releaseKey(product, platform)">
                  <div>
                    <a :href="release.url" class="uk-button uk-button-primary">
                      <div class="uk-grid-small uk-flex-middle" uk-grid>
                        <div class="uk-width-auto">
                          <font-awesome-icon :icon="['fab', platform === 'mac' ? 'apple' : 'windows']" size="4x" />
                        </div>

                        <div class="uk-width-auto">
                          <div class="uk-margin-remove">Download Latest</div>
                          <div class="uk-margin-remove download-subtitle uk-text-lowercase">
                            v{{release.version}} &mdash;
                            <time :datetime="release.pub_date">{{new Date(release.pub_date).toLocaleDateString()}}</time>
                          </div>
                        </div>
                      </div>
                    </a>
                  </div>
                  <a class="uk-button uk-button-link uk-text-meta" @click="showChangelog(product, platform)">Release Notes &amp; Older Versions</a>
                </div>
              </div>
          </div>
        </div>
        <table class="uk-table uk-table-striped uk-table-middle uk-table-hover">
          <thead>
            <tr>
              <th>Code</th>
              <th class="uk-text-center">Purchase Date</th>
              <th class="uk-text-center"># Activations</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="keyCode in product.codes" :key="keyCode.code">
              <td>
                <button class="uk-button uk-button-link" uk-tooltip="Copy to clipboard" v-clipboard="keyCode.code" @success="showCopySuccess"><font-awesome-icon icon="clipboard" size="2x" /></button>
                <span @click="selectKeyCode">{{keyCode.code}}</span>
              </td>
              <td class="uk-text-center"><time :datetime="keyCode.creationDate">{{new Date(keyCode.creationDate).toLocaleDateString()}}</time></td>
              <td class="uk-text-center">{{keyCode.totalAuthorizations}}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <!-- Products Section -->

    <div class="uk-child-width-1-2@m" uk-grid v-if="user">
      <!-- User Info Section -->
      <div>
        <h2 class="uk-margin">Your Info</h2>
        <div class="uk-card uk-card-default uk-card-hover uk-card-body">
          <form class="uk-form-stacked">
            <table class="uk-table">
              <tbody>
                <tr>
                  <td class="uk-table-shrink uk-text-uppercase uk-text-meta">Name</td>
                  <td>{{user.firstName}} {{user.lastName}}</td>
                </tr>
                <tr>
                  <td class="uk-table-shrink uk-text-uppercase uk-text-meta">Email</td>
                  <td>{{user.email}}</td>
                </tr>
                <tr>
                  <td class="uk-table-shrink uk-text-uppercase uk-text-meta">Password</td>
                  <td><a class="uk-button uk-button-link" @click="askChangePassword">&bullet;&bullet;&bullet;&bullet;&bullet;&bullet;&bullet;&bullet;<span uk-icon="icon: pencil"></span></a></td>
                </tr>
              </tbody>
            </table>
          </form>
        </div>
      </div>
      <!-- User Info Section -->

      <!-- Payments Section -->
      <div>
        <template>
          <h2 class="uk-margin">Your Payments</h2>
          <div class="uk-card uk-card-default uk-card-hover uk-card-body">
            <table class="uk-table uk-table-striped uk-table-middle uk-table-hover">
              <thead>
                <tr>
                  <th>Date</th>
                  <th class="uk-text-center">Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, index) in payments" :key="index">
                  <td>
                    <time :datetime="item.created">{{new Date(item.created*1000).toLocaleDateString()}}</time>
                  </td>
                  <td class="uk-text-center">
                    <span :class="{ refunded: item.refunded }">${{item.amount/100}}</span>
                    <span v-if="item.refunded">
                      &nbsp;$0
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>
      </div>
      <!-- Payments Section -->
    </div>

    <!-- Change Password Dialog -->
    <div ref="change-password" uk-modal>
      <div class="uk-modal-dialog uk-margin-auto-vertical" :class="{ 'uk-animation-shake': shakeForm }">
        <button class="uk-modal-close-default" type="button" uk-close></button>
        <div class="uk-modal-header">
          <h2 class="uk-modal-title">Change Password</h2>
        </div>
        <div class="uk-modal-body">
          <form id="change-password-form" class="uk-form-stacked" @submit.prevent="submitChangePassword">
            <div class="uk-margin">
              <label class="uk-form-label">Current Password</label>
              <div class="uk-form-controls">
                <input name="currentPassword" data-vv-as="current password"
                  v-model="currentPassword"
                  v-validate="{ required: true, min: 6 }"
                  :class="{ 'uk-form-danger': errors.has('currentPassword') }"
                  class="uk-input"
                  type="password"
                  placeholder="Your current password">
              </div>
              <div v-show="errors.has('currentPassword')" class="uk-text-danger">{{ errors.first('currentPassword') }}</div>
            </div>
            <div class="uk-margin">
              <label class="uk-form-label">New Password</label>
              <div class="uk-form-controls">
                <input name="newPassword" data-vv-as="new password"
                  v-model="newPassword"
                  v-validate="{ required: true, min: 6 }"
                  :class="{ 'uk-form-danger': errors.has('newPassword') }"
                  class="uk-input"
                  type="password"
                  placeholder="Your new password">
              </div>
              <div v-show="errors.has('newPassword')" class="uk-text-danger">{{ errors.first('newPassword') }}</div>
            </div>
            <div class="uk-margin">
              <label class="uk-form-label">Confirm New Password</label>
              <div class="uk-form-controls">
                <input name="newPasswordConfirm" data-vv-as="new password confirmation"
                  v-model="newPasswordConfirm"
                  v-validate="{ required: true, min: 6, is: newPassword }"
                  :class="{ 'uk-form-danger': errors.has('newPasswordConfirm') }"
                  class="uk-input"
                  type="password"
                  placeholder="Your new password again, for safety">
              </div>
              <div v-show="errors.has('newPasswordConfirm')" class="uk-text-danger">{{ errors.first('newPasswordConfirm') }}</div>
            </div>
          </form>
        </div>
        <div class="uk-modal-footer">
          <button class="uk-button uk-button-default uk-modal-close">Cancel</button>
          <button class="uk-button uk-button-primary uk-float-right" form="change-password-form">Change</button>
        </div>
      </div>
    </div>
    <!-- Change Password Dialog -->

    <!-- Release Notes Dialog -->
    <div ref="changelog" uk-modal>
      <div class="uk-modal-dialog uk-margin-auto-vertical" v-if="currentProduct && currentPlatform">
        <button class="uk-modal-close-default" type="button" uk-close></button>
        <div class="uk-modal-header">
          <h2 class="uk-modal-title uk-text-capitalize">{{fullProductName(currentProduct)}} Release Notes ({{currentPlatform}})</h2>
        </div>
        <div class="uk-modal-body" uk-overflow-auto>
          <dl v-for="release in latestReleases(currentProduct, currentPlatform)" :key="release.version" class="uk-description-list">
            <dt class="uk-position-relative uk-text-capitalize">
              <a :href="release.url" class="uk-text-large"><span uk-icon="icon: download" ratio="1"></span>{{release.version}}</a>
              <span class="uk-text-meta uk-position-center-right">
                Released&nbsp;
                <time datetime="release.pub_date">{{new Date(release.pub_date).toLocaleDateString()}}</time>
              </span>
            </dt>
            <dd class="uk-margin-left text-smaller-85">
              <ul class="uk-list uk-list-bullet">
                <li v-for="(notes, i) in release.notes" :key="i" class="uk-margin-remove-top">{{notes}}</li>
              </ul>
            </dd>
          </dl>
        </div>
      </div>
    </div>
    <!-- Release Notes Dialog -->

  </div>
</template>
<script>
import { mapActions } from 'vuex';
import Vue from 'vue';
import UIKit from 'uikit';

import FontAwesomeIcon from '@fortawesome/vue-fontawesome';

export default {
  data() {
    return {
      waitingForUserData: true,
      user: null,
      products: [],
      releases: [],
      payments: [],
      platforms: ['mac', 'windows'],
      shakeForm: false,
      currentPassword: '',
      newPassword: '',
      newPasswordConfirm: '',
      currentProduct: {},
      currentPlatform: ''
    };
  },
  beforeMount() {
    this.fetchUserData().then((response) => {
      this.waitingForUserData = false;
      this.products = response.data.products;
      this.user = response.data.user;
      this.products.forEach(({name, version}) => {
        this.platforms.forEach((platform) => {
          this.fetchReleaseData({ name, version, platform, channel: 'release' }).then((response) => {
            Vue.set(this.releases, this.releaseKey(name, version, platform), response.data);
          });
        });
      });
    });
    this.fetchPaymentHistory().then((response) => {
      this.payments = response.data;
    });
  },
  mounted() {
    if (this.$route.query.token) {
      UIKit.modal(this.$refs['reset-password']).show();
    }
  },
  methods: {
    ...mapActions([
      'fetchUserData',
      'fetchReleaseData',
      'fetchPaymentHistory',
      'changePassword'
    ]),
    releaseKey(name, version, platform) {
      return `${name} ${version} ${platform}`;
    },
    fullProductName(product) {
      return product.name + (product.version > 1 ? ' ' + product.version : '');
    },
    latestReleases(product, platform) {
      return this.releases[this.releaseKey(product.name, product.version, platform)];
    },
    latestReleasePerPlatform(product) {
      return this.platforms.reduce((result, platform) => {
        const releases = this.latestReleases(product, platform);
        result[platform] = (releases && releases.length > 0) ? releases[0] : null;
        return result;
      }, {});
    },
    showCopySuccess() {
      UIKit.notification('<span uk-icon="icon: check"></span> Key code copied!', { status: 'success' });
    },
    selectKeyCode(e) {
      const range = document.createRange();
      range.selectNodeContents(e.target);
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
    },
    askChangePassword() {
      this.errors.clear();
      this.currentPassword = this.newPassword = this.newPassword = '';
      UIKit.modal(this.$refs['change-password']).show();
    },
    submitChangePassword() {
      this.$validator.validateAll().then((result) => {
        if (!result) {
          this.shakeForm = true;
          setTimeout(() => { this.shakeForm = false; }, 1000);
          return;
        }
        UIKit.modal(this.$refs['change-password']).hide();
        this.changePassword({
          authorizer: { password: this.currentPassword },
          newPassword: this.newPassword,
          newPasswordConfirm: this.newPasswordConfirm
        }).then(() => {
          UIKit.notification('<span uk-icon="icon: check"></span> Password changed!', { status: 'success' });
        }).catch((error) => {
          const errorMessage = error.response ? `${error.response.data} [#${error.response.status}]` : error.message;
          UIKit.notification(
            `<span uk-icon="icon: warning"></span> Uh oh... your password couldn't be changed for some reason! Please try again later.
            <br>
            <span class="uk-text-muted uk-text-meta">${errorMessage}</span>`,
            { status: 'danger' });
        });
      });
    },
    showChangelog(product, platform) {
      this.currentProduct = product;
      this.currentPlatform = platform;
      UIKit.modal(this.$refs['changelog']).show();
    }
  },
  components: {
    FontAwesomeIcon
  }
};
</script>
<style>
.download-subtitle {
  line-height: 0px;
  font-size: 85%;
  opacity: 0.5;
}

.text-smaller-85 {
  font-size: 85%;
}

.refunded {
  text-decoration: line-through;
  text-decoration: red solid line-through;
  font-style: italic;
}
</style>



// WEBPACK FOOTER //
// src/components/UserPortal.vue